import React, { useState } from 'react';
import { Cookie, Settings, X, Check, AlertTriangle, Eye, Database, Target } from 'lucide-react';
import { useCookie } from '../contexts/CookieContext';

const CookieConsent: React.FC = () => {
  const { preferences, showBanner, updatePreferences, acceptAll, rejectAll } = useCookie();
  const [showSettings, setShowSettings] = useState(false);

  const handleSavePreferences = () => {
    updatePreferences(preferences);
    setShowSettings(false);
  };

  const handlePreferenceChange = (key: keyof typeof preferences, value: boolean) => {
    if (key === 'essential') return; // Essential cookies cannot be disabled
    updatePreferences({
      ...preferences,
      [key]: value,
    });
  };

  // Don't render at all when hidden to prevent any space reservation
  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Backdrop overlay on mobile to prevent interaction with background content */}
      <div 
        className="fixed inset-0 z-[99998] bg-black/20 backdrop-blur-sm md:hidden"
        style={{ 
          willChange: 'opacity',
          pointerEvents: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
        aria-hidden="true"
      />
      
      {/* Cookie Banner - Only render when visible to prevent white space */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[99999] bg-white border-t border-gray-200 shadow-2xl transform transition-transform duration-300 cookie-banner-visible"
        style={{ 
          willChange: 'transform',
          transform: 'translateY(0)',
          visibility: 'visible',
          position: 'fixed',
          // Ensure it's above everything on mobile
          isolation: 'isolate'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
              <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500 flex-shrink-0 mt-0.5 sm:mt-1" />
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                  We use cookies to enhance your experience
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 leading-relaxed">
                  We use cookies to provide you with a better experience, analyze site traffic, and personalize content. 
                  You can choose which cookies to accept or reject.
                </p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  <button
                    onClick={() => setShowSettings(true)}
                    className="inline-flex items-center px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:opacity-90 transition-opacity duration-200 will-change-transform active:scale-[0.98]"
                    style={{ transform: 'translateZ(0)' }}
                  >
                    <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                    <span className="hidden xs:inline">Cookie Settings</span>
                    <span className="xs:hidden">Settings</span>
                  </button>
                  <a
                    href="/cookie-policy"
                    className="inline-flex items-center px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-blue-600 hover:opacity-80 transition-opacity duration-200 will-change-transform active:scale-[0.98]"
                    style={{ transform: 'translateZ(0)' }}
                    aria-label="Read our Cookie Policy to learn more about how we use cookies"
                  >
                    <span className="hidden sm:inline">Read Cookie Policy</span>
                    <span className="sm:hidden">Policy</span>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="flex flex-row sm:flex-row gap-2 w-full sm:w-auto lg:ml-4">
              <button
                onClick={rejectAll}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:opacity-90 transition-opacity duration-200 will-change-transform active:scale-[0.98]"
                style={{ transform: 'translateZ(0)' }}
              >
                Reject All
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-purple-600 rounded-md hover:scale-105 transition-transform duration-200 shadow-md hover:shadow-lg will-change-transform active:scale-[0.98]"
                style={{ transform: 'translateZ(0)' }}
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[99999] bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ isolation: 'isolate' }}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-orange-500" />
                Cookie Preferences
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:opacity-70 transition-opacity duration-200 will-change-transform"
                style={{ transform: 'translateZ(0)' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <p className="text-sm text-gray-600">
                Manage your cookie preferences below. You can enable or disable different types of cookies.
              </p>

              {/* Essential Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-3 text-red-500" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Essential Cookies</h3>
                      <p className="text-sm text-gray-600">Required for basic website functionality</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-2" />
                    <span className="text-sm text-gray-500">Always Active</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  These cookies are necessary for the website to function and cannot be disabled.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <Eye className="w-5 h-5 mr-3 text-blue-500" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Analytics Cookies</h3>
                      <p className="text-sm text-gray-600">Help us understand how you use our website</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  These cookies collect information about how you use our website, which pages you visit, and any errors you encounter.
                </p>
              </div>

              {/* Functional Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <Database className="w-5 h-5 mr-3 text-purple-500" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Functional Cookies</h3>
                      <p className="text-sm text-gray-600">Enable enhanced features and personalization</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  These cookies enable enhanced functionality and personalization, such as remembering your preferences.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <Target className="w-5 h-5 mr-3 text-green-500" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Marketing Cookies</h3>
                      <p className="text-sm text-gray-600">Used to deliver relevant advertisements</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  These cookies are used to deliver advertisements that are relevant to you and your interests.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:opacity-90 transition-opacity duration-200 will-change-transform"
                style={{ transform: 'translateZ(0)' }}
              >
                Cancel
              </button>
              <div className="flex gap-2">
                <button
                  onClick={rejectAll}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:opacity-90 transition-opacity duration-200 will-change-transform"
                  style={{ transform: 'translateZ(0)' }}
                >
                  Reject All
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-purple-600 rounded-md hover:scale-105 transition-transform duration-200 shadow-md hover:shadow-lg will-change-transform"
                  style={{ transform: 'translateZ(0)' }}
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
